import React, {
  createContext, useCallback, useContext, useMemo,
} from 'react';
import type { PropsWithChildren } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { selectLatestEventsFilters } from '../selectors.js';
import { updateFilters as updateWorkspaceFilters } from '../store.js';
import type { VideoAnalyticType, WorkspaceFilters } from './workspaceFiltersModel';

interface WorkspaceFiltersContextValue {
  filters: WorkspaceFilters;
  setFilters: (filters: WorkspaceFilters) => void;
  updateFilters: (filters: Partial<WorkspaceFilters>) => void;
  updateVideoAnalyticsFilters: (
    analyticType: VideoAnalyticType,
    filters: Record<string, unknown>,
  ) => void;
}

interface WorkspaceFiltersProviderProps extends PropsWithChildren {
  initialData: WorkspaceFilters;
}

const WorkspaceFiltersContext = createContext<WorkspaceFiltersContextValue | undefined>(undefined);

function WorkspaceFiltersProvider({
  children,
  initialData,
}: WorkspaceFiltersProviderProps): React.JSX.Element {
  const dispatch = useDispatch();
  const storedFilters = useSelector(selectLatestEventsFilters) as Partial<WorkspaceFilters>;

  const filters = useMemo<WorkspaceFilters>(() => ({
    ...initialData,
    ...storedFilters,
  }), [initialData, storedFilters]);

  const setFilters = useCallback((nextFilters: WorkspaceFilters) => {
    dispatch(updateWorkspaceFilters({ filters: nextFilters }));
  }, [dispatch]);

  const updateFilters = useCallback((nextFilters: Partial<WorkspaceFilters>) => {
    setFilters({
      ...filters,
      ...nextFilters,
    });
  }, [filters, setFilters]);

  const updateVideoAnalyticsFilters = useCallback((
    analyticType: VideoAnalyticType,
    nextFilters: Record<string, unknown>,
  ) => {
    const analytic = filters.videoAnalytics[analyticType];
    updateFilters({
      videoAnalytics: {
        ...filters.videoAnalytics,
        [analyticType]: {
          ...analytic,
          filters: {
            ...analytic.filters,
            ...nextFilters,
          },
        },
      },
    });
  }, [filters.videoAnalytics, updateFilters]);

  const contextValue = useMemo<WorkspaceFiltersContextValue>(() => ({
    filters,
    setFilters,
    updateFilters,
    updateVideoAnalyticsFilters,
  }), [filters, setFilters, updateFilters, updateVideoAnalyticsFilters]);

  return (
    <WorkspaceFiltersContext.Provider value={contextValue}>
      {children}
    </WorkspaceFiltersContext.Provider>
  );
}

function useWorkspaceFilters(): WorkspaceFiltersContextValue {
  const context = useContext(WorkspaceFiltersContext);
  if (context === undefined) {
    throw new Error('useWorkspaceFilters must be used with WorkspaceFiltersProvider');
  }
  return context;
}

export {
  useWorkspaceFilters,
  WorkspaceFiltersProvider,
};
